import { useMemo } from 'react';
import styles from './styles.module.scss';
import { type UpgradeConfigKey, type UpgradeType } from '../../types/game-types';
import { upgradesConfig } from '../../upgrades-config';
import { GeneratorContextProvider } from '../../store/generator-context';
import { ComposableIconsProvider } from '@game-libs/exc-libs/src/composable-icons';
import { spritesheetConfig } from '../../config';
import { UpgradesListItem } from '../UpgradesListItem';
import { TreeNodeData } from 'skill-tree/src/models';
import { useUpgradesStore } from '../../store/zustand/useUpgradesStore';
import { ScrollArea } from 'radix-ui';

interface ListViewProps {
  selectedElement: UpgradeConfigKey;
}

interface FlattenedSkill {
  skill: TreeNodeData;
  parentId: string | null;
}

// Flatten the skill tree into a list of skills (breadth-first order)
const flattenSkills = (skills: TreeNodeData[]): FlattenedSkill[] => {
  if (skills.length === 0) return [];

  const nodeMap = new Map(skills.map((node) => [node.id, node]));
  const result: FlattenedSkill[] = [];
  const visited = new Set<string>();
  const queue: { node: TreeNodeData; parentId: string | null }[] = [{ node: skills[0], parentId: null }];

  while (queue.length > 0) {
    const { node, parentId } = queue.shift()!;
    if (visited.has(node.id)) continue;
    visited.add(node.id);
    result.push({ skill: node, parentId });

    for (const childId of node.children) {
      const childNode = nodeMap.get(childId);
      if (childNode && !visited.has(childId)) {
        queue.push({ node: childNode, parentId: node.id });
      }
    }
  }

  return result;
};

export const ListView = ({ selectedElement }: ListViewProps) => {
  const upgrades = useUpgradesStore((store) => store.upgrades);
  const config = upgradesConfig[selectedElement];
  const flattenedSkills = useMemo(() => (config ? flattenSkills(config) : []), [config]);

  // Filter out skills that don't have corresponding upgrade data
  const validSkills = flattenedSkills.filter(({ skill }) => upgrades[selectedElement]?.[skill.id as UpgradeType] !== undefined);

  return (
    <ScrollArea.Root type="always" className={styles.upgradesList}>
      <ScrollArea.Viewport className={styles.scrollViewport}>
        <div className={styles.scrollContent}>
          <ComposableIconsProvider value={spritesheetConfig}>
            <GeneratorContextProvider value={selectedElement}>
              {validSkills.map(({ skill, parentId }) => (
                <UpgradesListItem key={skill.id} skill={skill} parentId={parentId} />
              ))}
            </GeneratorContextProvider>
          </ComposableIconsProvider>
        </div>
      </ScrollArea.Viewport>
      <ScrollArea.Scrollbar className={styles.scrollbar} orientation="vertical">
        <ScrollArea.Thumb className={styles.scrollThumb} />
      </ScrollArea.Scrollbar>
      <ScrollArea.Corner />
    </ScrollArea.Root>
  );
};
